import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  FileText,
  Download,
  Edit,
  AlertCircle
} from 'lucide-react';
import { driverApplicationAPI } from '../../services/driverApplicationAPI';
import { DriverApplication } from '../../types';

const DriverApplicationStatus: React.FC = () => {
  const [application, setApplication] = useState<DriverApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await driverApplicationAPI.getMyApplication();
      setApplication(response.application);
    } catch (err) {
      if (err instanceof Error && err.message.includes('No driver application found')) {
        setApplication(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch application');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (documentId: string, filename: string) => {
    try {
      if (!application) return;
      
      const blob = await driverApplicationAPI.downloadDocument(application.id, documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-6 w-6 text-yellow-600" />;
      case 'approved':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-6 w-6 text-red-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Your driver application is being reviewed by our team. This typically takes 1-3 business days.';
      case 'approved':
        return 'Congratulations! Your driver application has been approved. You can now create trips and routes.';
      case 'rejected':
        return 'Your driver application was not approved at this time. Please review the comments below and consider reapplying.';
      default:
        return 'Unknown application status.';
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">Error</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6 text-center">
          <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Driver Application Found</h2>
          <p className="text-gray-600 mb-6">
            You haven't submitted a driver application yet. Apply now to become a verified driver.
          </p>
          <button
            onClick={() => window.location.href = '/apply-driver'}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Apply to Become a Driver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Status Overview */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Driver Application Status</h1>
        </div>

        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            {getStatusIcon(application.application_status)}
            <div>
              <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.application_status)}`}>
                {application.application_status.charAt(0).toUpperCase() + application.application_status.slice(1)}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Submitted on {new Date(application.submitted_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-gray-700">{getStatusMessage(application.application_status)}</p>
          </div>

          {application.application_status === 'rejected' && application.admin_comments && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-red-900 mb-2">Admin Comments</h3>
              <p className="text-red-700">{application.admin_comments}</p>
            </div>
          )}

          {application.application_status === 'approved' && application.admin_comments && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-green-900 mb-2">Admin Comments</h3>
              <p className="text-green-700">{application.admin_comments}</p>
            </div>
          )}

          {application.reviewed_at && (
            <p className="text-sm text-gray-600">
              Reviewed on {new Date(application.reviewed_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Application Details */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Application Details</h2>
          {application.application_status === 'pending' && (
            <button
              onClick={() => window.location.href = '/apply-driver/edit'}
              className="flex items-center space-x-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
            >
              <Edit className="h-4 w-4" />
              <span>Edit Application</span>
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Driver License */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Driver License Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">License Number:</span>
                <span className="ml-2 font-medium">{application.license_number}</span>
              </div>
              <div>
                <span className="text-gray-600">Expiry Date:</span>
                <span className="ml-2 font-medium">
                  {new Date(application.license_expiry_date).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Vehicle Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Make:</span>
                <span className="ml-2 font-medium">{application.vehicle_make}</span>
              </div>
              <div>
                <span className="text-gray-600">Model:</span>
                <span className="ml-2 font-medium">{application.vehicle_model}</span>
              </div>
              <div>
                <span className="text-gray-600">Year:</span>
                <span className="ml-2 font-medium">{application.vehicle_year}</span>
              </div>
              <div>
                <span className="text-gray-600">Registration:</span>
                <span className="ml-2 font-medium">{application.vehicle_registration}</span>
              </div>
              <div>
                <span className="text-gray-600">Color:</span>
                <span className="ml-2 font-medium">{application.vehicle_color}</span>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Name:</span>
                <span className="ml-2 font-medium">{application.emergency_contact_name}</span>
              </div>
              <div>
                <span className="text-gray-600">Phone:</span>
                <span className="ml-2 font-medium">{application.emergency_contact_phone}</span>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          {(application.previous_experience || application.reason_for_driving) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Additional Information</h3>
              {application.previous_experience && (
                <div className="mb-4">
                  <span className="text-gray-600 text-sm">Previous Experience:</span>
                  <p className="mt-1 text-sm text-gray-900">{application.previous_experience}</p>
                </div>
              )}
              {application.reason_for_driving && (
                <div>
                  <span className="text-gray-600 text-sm">Reason for Driving:</span>
                  <p className="mt-1 text-sm text-gray-900">{application.reason_for_driving}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Documents */}
      {application.documents && application.documents.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Uploaded Documents</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {application.documents.map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-8 w-8 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.original_name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.type.replace('_', ' ')} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDownloadDocument(doc.id, doc.original_name)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverApplicationStatus;
